import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { InstructionGuide } from '../components/layout/InstructionGuide';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { CalendarDays, Plus, CheckCircle2, Clock, XCircle, Eye, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function ClientVisits() {
  const { api, currentUser, userZone, roleLevel, selectedBusiness } = useApp();
  const [visits, setVisits] = useState([]);
  const [salesPoints, setSalesPoints] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailVisit, setDetailVisit] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({
    sales_point_id: '', user_id: '', visit_date: '', visit_type: 'routine', purpose: '', status: 'planned', notes: '', outcome: ''
  });

  const load = useCallback(async () => {
    try {
      const visitParams = {};
      if (roleLevel >= 6) visitParams.user_id = currentUser?.id;
      if (selectedBusiness) visitParams.business_id = selectedBusiness;
      const spParams = userZone ? { zone: userZone } : {};
      if (selectedBusiness) spParams.business_id = selectedBusiness;
      const [vRes, spRes, uRes] = await Promise.all([
        api.getVisits(visitParams),
        api.getSalesPoints(spParams),
        api.getUsers(userZone ? { zone: userZone } : {}),
      ]);
      setVisits(vRes.data);
      setSalesPoints(spRes.data);
      setUsers(uRes.data.data || uRes.data);
    } catch (e) { console.error(e); }
  }, [api, currentUser, roleLevel, userZone, selectedBusiness]);

  useEffect(() => { load(); }, [load]);

  const filtered = visits.filter(v => statusFilter === 'all' || v.status === statusFilter);

  const dateVisits = filtered.filter(v => v.visit_date === format(selectedDate, 'yyyy-MM-dd'));

  const visitDates = visits.map(v => v.visit_date);

  const openCreate = () => {
    setForm({
      sales_point_id: salesPoints[0]?.id || '',
      user_id: currentUser?.id || users[0]?.id || '',
      visit_date: format(selectedDate, 'yyyy-MM-dd'),
      visit_type: 'routine',
      purpose: '',
      status: 'planned',
      notes: '',
      outcome: '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.sales_point_id || !form.user_id || !form.purpose) { toast.error('Fill all required fields'); return; }
    try {
      const data = { ...form };
      if (selectedBusiness) data.business_id = selectedBusiness;
      await api.createVisit(data);
      toast.success('Visit scheduled');
      setDialogOpen(false);
      load();
    } catch (e) { toast.error('Failed to create visit'); }
  };

  const handleStatusChange = async (visitId, newStatus) => {
    try {
      await api.updateVisit(visitId, { status: newStatus });
      toast.success(`Visit marked as ${newStatus}`);
      load();
    } catch (e) { toast.error('Failed to update'); }
  };

  const getSPName = (id) => salesPoints.find(s => s.id === id)?.name || 'Unknown';
  const getUserName = (id) => users.find(u => u.id === id)?.name || 'Unknown';

  const statusIcon = (status) => {
    if (status === 'completed') return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
    if (status === 'cancelled') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-600" />;
  };

  const statusColor = (status) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (status === 'cancelled') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  return (
    <div data-testid="client-visits-page" className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Client Visits</h1>
          <p className="text-sm text-slate-500 mt-1">{visits.length} total visits</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={api.exportVisits(userZone, roleLevel >= 6 ? currentUser?.id : null)} download data-testid="export-visits-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <FileDown className="w-4 h-4" /> Export
          </a>
          <Button data-testid="schedule-visit-btn" onClick={openCreate} className="gap-2 bg-amber-600 hover:bg-amber-700 text-white shadow-md">
            <Plus className="w-4 h-4" /> Schedule Visit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-4 border border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-amber-600" /> Calendar
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              modifiers={{ hasVisit: visitDates.map(d => new Date(d + 'T00:00:00')) }}
              modifiersClassNames={{ hasVisit: 'bg-amber-100 text-amber-700 font-bold' }}
              className="rounded-md"
            />
          </CardContent>
        </Card>

        {/* Visits List */}
        <div className="lg:col-span-8 space-y-4">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            {['all', 'planned', 'completed', 'cancelled'].map(s => (
              <Button
                key={s}
                data-testid={`visit-filter-${s}`}
                variant={statusFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(s)}
                className={statusFilter === s ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'text-slate-600'}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </div>

          {/* Date visits */}
          <Card className="border border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-900">
                Visits on {format(selectedDate, 'MMM dd, yyyy')} ({dateVisits.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {dateVisits.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No visits on this date</p>
              ) : dateVisits.map(v => (
                <div key={v.id} data-testid={`visit-card-${v.id}`} className="p-4 rounded-lg border border-border/40 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {statusIcon(v.status)}
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{v.purpose}</p>
                        <p className="text-xs text-slate-400 mt-0.5">SP: {getSPName(v.sales_point_id)}</p>
                        <p className="text-xs text-slate-400">By: {getUserName(v.user_id)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${statusColor(v.status)}`}>{v.status}</Badge>
                      <Button variant="ghost" size="sm" onClick={() => setDetailVisit(v)} className="hover:bg-amber-50">
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  {v.status === 'planned' && (
                    <div className="flex gap-2 mt-3 ml-7">
                      <Button data-testid={`complete-visit-${v.id}`} size="sm" variant="outline" onClick={() => handleStatusChange(v.id, 'completed')} className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                        Complete
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleStatusChange(v.id, 'cancelled')} className="text-xs text-red-500 border-red-200 hover:bg-red-50">
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* All filtered visits */}
          <Card className="border border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-900">All Visits ({filtered.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-80 overflow-y-auto">
              {filtered.map(v => (
                <div key={v.id} className="flex items-center justify-between p-3 rounded-lg border border-border/20 hover:bg-slate-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {statusIcon(v.status)}
                    <div>
                      <p className="text-sm font-medium text-slate-700">{v.purpose}</p>
                      <p className="text-xs text-slate-400">{v.visit_date} - {getSPName(v.sales_point_id)}</p>
                    </div>
                  </div>
                  <Badge className={`text-xs ${statusColor(v.status)}`}>{v.status}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Schedule Visit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="schedule-visit-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Schedule Visit</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Sales Point</Label>
              <Select value={form.sales_point_id} onValueChange={v => setForm({ ...form, sales_point_id: v })}>
                <SelectTrigger data-testid="visit-sp-select"><SelectValue placeholder="Select sales point" /></SelectTrigger>
                <SelectContent>
                  {salesPoints.map(sp => <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned To</Label>
              <Select value={form.user_id} onValueChange={v => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="visit-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visit Date</Label>
              <Input data-testid="visit-date-input" type="date" value={form.visit_date} onChange={e => setForm({ ...form, visit_date: e.target.value })} />
            </div>
            <div>
              <Label>Visit Type</Label>
              <Select value={form.visit_type} onValueChange={v => setForm({ ...form, visit_type: v })}>
                <SelectTrigger data-testid="visit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="new_business">New Business</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Purpose</Label>
              <Input data-testid="visit-purpose-input" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="Purpose of visit" />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea data-testid="visit-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-visit-btn" onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700 text-white">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visit Detail Dialog */}
      <Dialog open={!!detailVisit} onOpenChange={() => setDetailVisit(null)}>
        <DialogContent data-testid="visit-detail-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Visit Details</DialogTitle>
          </DialogHeader>
          {detailVisit && (
            <div className="space-y-3 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-400">Purpose</p><p className="text-sm font-semibold text-slate-800">{detailVisit.purpose}</p></div>
                <div><p className="text-xs text-slate-400">Date</p><p className="text-sm font-semibold text-slate-800">{detailVisit.visit_date}</p></div>
                <div><p className="text-xs text-slate-400">Sales Point</p><p className="text-sm font-semibold text-slate-800">{getSPName(detailVisit.sales_point_id)}</p></div>
                <div><p className="text-xs text-slate-400">Assigned To</p><p className="text-sm font-semibold text-slate-800">{getUserName(detailVisit.user_id)}</p></div>
                <div><p className="text-xs text-slate-400">Type</p><Badge variant="secondary" className="text-xs mt-0.5">{detailVisit.visit_type}</Badge></div>
                <div><p className="text-xs text-slate-400">Status</p><Badge className={`text-xs mt-0.5 ${statusColor(detailVisit.status)}`}>{detailVisit.status}</Badge></div>
              </div>
              {detailVisit.notes && <div><p className="text-xs text-slate-400">Notes</p><p className="text-sm text-slate-600 mt-0.5">{detailVisit.notes}</p></div>}
              {detailVisit.outcome && <div><p className="text-xs text-slate-400">Outcome</p><Badge variant="outline" className="text-xs mt-0.5 capitalize">{detailVisit.outcome}</Badge></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
